import React, { useState, useEffect } from 'react';
import { storage } from '../config/firebase';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { Box, Typography, List, ListItem, Link as MuiLink, CircularProgress, Alert, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const UserFilesList = () => {
  const { currentUser } = useAuth();
  const [filesList, setFilesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      // setError('Please log in to see your files.'); // Or simply show nothing until logged in
      return;
    }

    const fetchFiles = async () => {
      setLoading(true);
      setError('');
      try {
        const userFilesPath = `userFiles/${currentUser.uid}/`;
        const listRef = ref(storage, userFilesPath);
        const res = await listAll(listRef);
        
        const filesPromises = res.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return { name: itemRef.name, url };
        });
        
        const fetchedFiles = await Promise.all(filesPromises);
        setFilesList(fetchedFiles);
      } catch (err) {
        console.error("Error fetching files:", err);
        if (err.code === 'storage/object-not-found' || err.code === 'storage/unknown') {
            // This can happen if the user's folder doesn't exist yet (no uploads)
            setFilesList([]); // Treat as no files found
        } else {
            setError('Failed to load your files. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [currentUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p:3, mt: 5 }}>
        <CircularProgress sx={{ color: '#5C3D2E' }} />
        <Typography sx={{ ml: 2, color: '#5C3D2E' }}>Loading your files...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3, backgroundColor: '#FFEBEE', color: '#C62828' }}>{error}</Alert>;
  }

  if (!currentUser) {
     return (
        <Box sx={{ p: 3, maxWidth: 600, margin: 'auto', mt: 5, textAlign: 'center', backgroundColor: '#F5EFE6', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ color: '#5C3D2E' }}>Please log in to view your uploaded files.</Typography>
            <Button component={RouterLink} to="/login" variant="contained" sx={{ mt: 2, backgroundColor: '#6D4C41', '&:hover': { backgroundColor: '#5D4037' } }}>
                Login
            </Button>
        </Box>
     );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: 'auto', mt: 5, backgroundColor: '#F5EFE6', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)' }}>
      <Typography variant="h5" sx={{ mb: 3, color: '#5C3D2E', textAlign: 'center', fontWeight: 'bold' }}>Your Uploaded Files</Typography>
      {filesList.length === 0 ? (
        <Box sx={{textAlign: 'center'}}>
            <Typography sx={{ color: '#7A5C4E' }}>You haven't uploaded any files yet.</Typography>
            <Button component={RouterLink} to="/upload" variant="outlined" sx={{ mt: 2, color: '#6D4C41', borderColor: '#6D4C41', '&:hover': {backgroundColor: 'rgba(109, 76, 65, 0.04)', borderColor: '#5D4037'} }}>
                Upload a File
            </Button>
        </Box>
      ) : (
        <List>
          {filesList.map((file) => (
            <ListItem 
              key={file.name} 
              sx={{ 
                borderBottom: '1px solid #D7CCC8', 
                '&:last-child': { borderBottom: 'none' },
                py: 1.5
              }}
            >
              <MuiLink 
                href={file.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                sx={{ 
                  color: '#6D4C41', 
                  textDecoration: 'none', 
                  fontWeight: 'medium',
                  '&:hover': { textDecoration: 'underline', color: '#4E342E' } 
                }}
              >
                {file.name}
              </MuiLink>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default UserFilesList;
